import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { api, BACKEND_ORIGIN } from "@/lib/api";
import { Truck, MapPin, AlertCircle } from "lucide-react";
import { localToday } from "@/lib/dates";
import { useAuth } from "@/context/AuthContext";

/**
 * TV-optimised Live Dispatch board. Full-screen, large fonts, no chrome —
 * intended for a monitor on the plant floor. Read-only: no drag/drop, no
 * dialogs, no navigation.
 *
 *   URL: /live         — auto-selects today, no plant filter
 *   URL: /live?plant=xxx (optional filter)
 */

const WS_URL = (() => {
  const base = BACKEND_ORIGIN || "";
  const u = new URL(base, window.location.origin);
  u.protocol = u.protocol === "https:" ? "wss:" : "ws:";
  u.pathname = "/api/ws";
  return u.toString();
})();

const STAGE_ORDER = ["available", "loading", "slumping", "en_route", "on_site", "discharging", "returning", "out_of_service"];
const STAGE_META = {
  available:      { label: "AVAILABLE",  color: "#71717A" },
  loading:        { label: "LOADING",    color: "#EAB308" },
  slumping:       { label: "SLUMPING",   color: "#FBBF24" },
  en_route:       { label: "TO SITE",    color: "#F97316" },
  on_site:        { label: "ON JOB",     color: "#22C55E" },
  discharging:    { label: "ON JOB",     color: "#22C55E" },
  returning:      { label: "RETURNING",  color: "#3B82F6" },
  out_of_service: { label: "OUT OF SVC", color: "#525252" },
};

function computeLoadSlots(startTime, trucksNeeded, spacingMin) {
  if (!startTime || !trucksNeeded || !spacingMin) return [];
  const [hh, mm] = startTime.split(":").map((n) => parseInt(n, 10));
  if (isNaN(hh) || isNaN(mm)) return [];
  return Array.from({ length: trucksNeeded }, (_, i) => {
    const total = hh * 60 + mm + i * spacingMin;
    const H = Math.floor((total / 60) % 24);
    const M = total % 60;
    return `${String(H).padStart(2, "0")}:${String(M).padStart(2, "0")}`;
  });
}

function computeNextLoad(job, dockets, nowMs) {
  const spacing = Number(job.load_spacing_min || 0);
  const trucks = Number(job.trucks_needed || 0);
  if (!spacing || !trucks || !job.time_required) return null;
  const issued = (dockets || []).filter((d) => d.job_id === job.id && d.status !== "cancelled").length;
  if (issued >= trucks) return { allDone: true };
  const slots = computeLoadSlots(job.time_required, trucks, spacing);
  const nextTime = slots[issued];
  if (!nextTime) return null;
  const [H, M] = nextTime.split(":").map((n) => parseInt(n, 10));
  const scheduled = new Date();
  scheduled.setHours(H, M, 0, 0);
  const minutesUntil = Math.round((scheduled.getTime() - nowMs) / 60000);
  return { nextTime, minutesUntil, pulse: minutesUntil <= 5, slotIndex: issued + 1, totalSlots: trucks };
}

export default function LiveDispatch() {
  const today = localToday();
  const { org } = useAuth();
  const [dash, setDash] = useState(null);
  const [trucks, setTrucks] = useState([]);
  const [dockets, setDockets] = useState([]);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const wsRef = useRef(null);
  const debounceRef = useRef(null);

  const load = useCallback(() => {
    Promise.all([
      api.get(`/dashboard?date=${today}`).then((r) => r.data),
      api.get("/trucks").then((r) => r.data),
      api.get(`/dockets`).then((r) => r.data),
    ]).then(([d, t, dk]) => {
      setDash(d);
      setTrucks(Array.isArray(t) ? t : t.items || []);
      setDockets(Array.isArray(dk) ? dk : dk.items || []);
    }).catch(() => {});
  }, [today]);

  useEffect(() => { load(); }, [load]);

  // Live tick — pulse timing + clock display
  useEffect(() => {
    const t = setInterval(() => setNowMs(Date.now()), 15000);
    return () => clearInterval(t);
  }, []);

  // Auto refresh every 30s regardless of WS (safety net for a wall display)
  useEffect(() => {
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  // WebSocket — reload on any dispatch event, debounced
  useEffect(() => {
    let ws;
    let closed = false;
    const debounced = () => {
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(load, 400);
    };
    const connect = () => {
      try {
        ws = new WebSocket(WS_URL);
        wsRef.current = ws;
        ws.onmessage = () => debounced();
        ws.onclose = () => { if (!closed) setTimeout(connect, 4000); };
      } catch { setTimeout(connect, 4000); }
    };
    connect();
    return () => { closed = true; try { ws && ws.close(); } catch {} };
  }, [load]);

  const jobs = useMemo(() => (dash?.jobs || []).filter((j) => j.status !== "cancelled"), [dash]);
  const rankedJobs = useMemo(() => {
    const rank = { scheduled: 1, in_progress: 0, completed: 2 };
    return [...jobs].sort((a, b) => (rank[a.status] ?? 3) - (rank[b.status] ?? 3) || (a.time_required || "").localeCompare(b.time_required || ""));
  }, [jobs]);

  const trucksByLane = useMemo(() => {
    const m = {};
    STAGE_ORDER.forEach((k) => (m[k] = []));
    trucks.forEach((t) => { const k = STAGE_ORDER.includes(t.status) ? t.status : "available"; (m[k] || m.available).push(t); });
    return m;
  }, [trucks]);

  const totalOrdered = jobs.reduce((s, j) => s + Number(j.quantity_m3 || 0), 0);
  const totalDelivered = jobs.reduce((s, j) => s + Number(j.delivered_m3 || 0), 0);
  const clock = new Date(nowMs).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });

  return (
    <div className="min-h-screen bg-black text-white p-6 flex flex-col gap-4" data-testid="live-dispatch">
      <style>{`
        @keyframes liveJobPulse { 0%,100% { background: rgba(255,90,0,0.10); box-shadow: inset 6px 0 0 0 rgba(255,90,0,0.55); } 50% { background: rgba(255,90,0,0.30); box-shadow: inset 6px 0 0 0 #ff5a00; } }
        .live-row-pulse { animation: liveJobPulse 1.6s ease-in-out infinite; }
      `}</style>

      {/* ── Header ─────────────────────────────────────────────────── */}
      <header className="flex items-end justify-between border-b border-neutral-800 pb-3">
        <div>
          <div className="mono text-[11px] uppercase tracking-[0.4em] text-neutral-500">LIVE DISPATCH · {org?.name || "ArmorBatch"}</div>
          <div className="heading text-5xl font-black uppercase tracking-tight">
            {today}
          </div>
        </div>
        <div className="text-right">
          <div className="mono text-[11px] uppercase tracking-widest text-neutral-500">Plant Time</div>
          <div className="mono text-6xl font-black text-primary-orange leading-none">{clock}</div>
        </div>
      </header>

      {/* ── Top KPI Strip ──────────────────────────────────────────── */}
      <div className="grid grid-cols-4 gap-3">
        <KPI label="Loads today" value={dash?.production_m3?.toFixed(1) || "0"} unit="m³ delivered" />
        <KPI label="Ordered" value={totalOrdered.toFixed(1)} unit="m³" />
        <KPI label="Trucks active" value={dash?.active_trucks ?? "0"} unit={`of ${trucks.length}`} />
        <KPI label="Progress" value={totalOrdered ? Math.round((totalDelivered / totalOrdered) * 100) + "%" : "—"} unit={`${totalDelivered.toFixed(1)} / ${totalOrdered.toFixed(1)} m³`} />
      </div>

      {/* ── Jobs Grid — the star of the show ───────────────────────── */}
      <div className="flex-1 grid grid-cols-1 xl:grid-cols-2 2xl:grid-cols-3 gap-3 overflow-y-auto pr-1">
        {rankedJobs.length === 0 && (
          <div className="col-span-full border-2 border-dashed border-neutral-800 p-16 text-center">
            <div className="mono text-xl text-neutral-500 uppercase tracking-widest">No jobs booked for today</div>
          </div>
        )}
        {rankedJobs.map((j) => {
          const nextLoad = computeNextLoad(j, dockets, nowMs);
          const attachedTrucks = trucks.filter((t) => (j.assigned_truck_ids || []).includes(t.id));
          const anyLoading = attachedTrucks.some((t) => t.status === "loading");
          const pulse = !!(nextLoad && nextLoad.pulse && !anyLoading);
          const pct = Math.min(100, ((j.delivered_m3 || 0) / (j.quantity_m3 || 1)) * 100);
          const done = j.status === "completed";
          return (
            <div
              key={j.id}
              data-testid={`live-job-${j.id}`}
              className={`border border-neutral-800 bg-neutral-950/60 p-4 flex flex-col gap-3 min-h-[240px] ${pulse ? "live-row-pulse border-primary-orange" : ""} ${done ? "opacity-45" : ""}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="mono text-[10px] uppercase tracking-widest text-primary-orange">{j.time_required || "any time"} · {j.mix_code}</div>
                  <div className="heading text-3xl font-black uppercase truncate">{j.customer_name}</div>
                  <div className="text-sm text-neutral-400 flex items-start gap-1 mt-0.5"><MapPin className="w-3.5 h-3.5 mt-1 shrink-0" /><span className="truncate">{j.site_address || j.site_name || "—"}</span></div>
                </div>
                <div className="text-right shrink-0">
                  <div className="mono text-4xl font-black">{Number(j.quantity_m3 || 0).toFixed(1)}<span className="text-lg text-neutral-500 ml-1">m³</span></div>
                  {j.slump_mm && <div className="mono text-[10px] text-neutral-500 uppercase">{j.slump_mm}mm slump</div>}
                </div>
              </div>

              {/* Progress */}
              <div>
                <div className="flex justify-between mono text-[11px] uppercase tracking-widest text-neutral-400 mb-1">
                  <span>Progress · {Number(j.delivered_m3 || 0).toFixed(1)} / {Number(j.quantity_m3 || 0).toFixed(1)} m³</span>
                  <span className={pct >= 100 ? "text-emerald-400" : "text-primary-orange"}>{pct.toFixed(0)}%</span>
                </div>
                <div className="h-2 bg-neutral-900">
                  <div className={pct >= 100 ? "h-full bg-emerald-500" : "h-full bg-primary-orange"} style={{ width: `${pct}%` }} />
                </div>
              </div>

              {/* Truck chips */}
              <div className="flex flex-wrap gap-1.5">
                {attachedTrucks.length === 0 && <span className="mono text-[10px] uppercase tracking-widest text-neutral-600 border border-dashed border-neutral-800 px-2 py-1">No trucks assigned</span>}
                {attachedTrucks.map((t) => {
                  const meta = STAGE_META[t.status] || STAGE_META.available;
                  return (
                    <span key={t.id} className="mono text-xs font-bold uppercase tracking-widest px-2 py-1 inline-flex items-center gap-1.5" style={{ color: meta.color, borderLeft: `3px solid ${meta.color}`, background: `${meta.color}18` }}>
                      <Truck className="w-3.5 h-3.5" />{t.number} · {meta.label}
                    </span>
                  );
                })}
              </div>

              {/* Next-load pulse chip */}
              {nextLoad && !nextLoad.allDone && !done && (
                <div className={`mt-auto flex items-center justify-between px-3 py-2 border ${pulse ? "border-primary-orange bg-primary-orange/10" : "border-neutral-800 bg-neutral-950"}`}>
                  <div className="mono text-[11px] uppercase tracking-widest text-neutral-400">
                    Next load <span className="text-white">#{nextLoad.slotIndex}/{nextLoad.totalSlots}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {pulse && <AlertCircle className="w-5 h-5 text-primary-orange animate-pulse" />}
                    <span className={`mono text-2xl font-black ${pulse ? "text-primary-orange" : "text-white"}`}>{nextLoad.nextTime}</span>
                    <span className={`mono text-xs uppercase tracking-widest ${nextLoad.minutesUntil < 0 ? "text-red-400" : pulse ? "text-primary-orange" : "text-neutral-500"}`}>
                      {nextLoad.minutesUntil < 0 ? "OVERDUE" : nextLoad.minutesUntil <= 15 ? `${nextLoad.minutesUntil}m` : ""}
                    </span>
                  </div>
                </div>
              )}
              {nextLoad?.allDone && !done && (
                <div className="mt-auto text-center mono text-xs uppercase tracking-widest text-emerald-500 border border-emerald-800 py-1.5">
                  All trucks dispatched
                </div>
              )}
              {done && (
                <div className="mt-auto text-center mono text-xs uppercase tracking-widest text-neutral-500 border border-neutral-800 py-1.5">
                  Completed
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ── Fleet strip along the bottom ────────────────────────────── */}
      <div className="shrink-0 border-t border-neutral-800 pt-3" data-testid="live-fleet-strip">
        <div className="mono text-[10px] uppercase tracking-[0.4em] text-neutral-500 mb-2">Fleet</div>
        <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${STAGE_ORDER.length}, minmax(0, 1fr))` }}>
          {STAGE_ORDER.map((k) => {
            const meta = STAGE_META[k];
            const laneTrucks = trucksByLane[k] || [];
            return (
              <div key={k} className="border border-neutral-800" style={{ borderColor: `${meta.color}55` }}>
                <div className="flex items-center justify-between px-2 py-1 border-b border-neutral-800" style={{ borderColor: `${meta.color}55`, background: `${meta.color}18` }}>
                  <span className="mono text-[10px] font-bold tracking-widest" style={{ color: meta.color }}>{meta.label}</span>
                  <span className="mono text-lg font-black" style={{ color: meta.color }}>{laneTrucks.length}</span>
                </div>
                <div className="p-1.5 flex flex-wrap gap-1 min-h-[38px]">
                  {laneTrucks.map((t) => (
                    <span key={t.id} className="mono text-xs font-bold px-1.5 py-0.5 border" style={{ borderColor: meta.color, color: meta.color }}>{t.number}</span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function KPI({ label, value, unit }) {
  return (
    <div className="border border-neutral-800 bg-neutral-950/60 p-4">
      <div className="mono text-[10px] uppercase tracking-widest text-neutral-500">{label}</div>
      <div className="mono text-5xl font-black text-primary-orange leading-none mt-1">{value}</div>
      <div className="mono text-[10px] uppercase tracking-widest text-neutral-500 mt-1">{unit}</div>
    </div>
  );
}
